import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowClockwise, CurrencyDollar, FloppyDisk, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@radix-ui/themes";

import { NumberField } from "../../components/NumberField";
import { useAdminRead } from "../../hooks/useAdminRead";
import { publishCostLimits, readAdminData } from "../../services/adminApi";

import type { CostLimitsData, TayanCostLimits } from "../../types";
import type { CostLimitFieldDefinition, CostLimitSectionDefinition } from "./index.types";

const sections: CostLimitSectionDefinition[] = [
  {
    title: "Hesap limitleri",
    description: "Her hesap için paylaşılan istek, token ve sağlayıcı maliyeti tavanları.",
    fields: [
      { key: "accountRequestsPerMinute", label: "Analiz / dakika", description: "Bir hesabın dakikada başlatabileceği yeni analiz sayısı.", minimum: 0, maximum: 10_000, unit: "count" },
      { key: "accountRequestsPerDay", label: "Analiz / UTC günü", description: "Bir hesabın UTC gününde başlatabileceği toplam analiz sayısı.", minimum: 0, maximum: 1_000_000, unit: "count" },
      { key: "accountTokensPerMonth", label: "Token / UTC ayı", description: "Başarılı analizlerde ölçülen giriş ve çıkış tokenlarının aylık toplamı.", minimum: 0, maximum: 1_000_000_000, unit: "count" },
      { key: "accountCostNanoUsdPerMonth", label: "Maliyet / UTC ayı", description: "Başarısız çıktı doğrulaması dahil ölçülen sağlayıcı maliyeti.", minimum: 0, maximum: 1_000_000_000_000, unit: "usd" },
    ],
  },
  {
    title: "Platform limitleri",
    description: "Tüm hesapların paylaştığı günlük istek ve maliyet korumaları.",
    fields: [
      { key: "projectRequestsPerDay", label: "Toplam analiz / UTC günü", description: "Tayan genelindeki günlük analiz başlatma tavanı.", minimum: 0, maximum: 10_000_000, unit: "count" },
      { key: "projectCostNanoUsdPerDay", label: "AI maliyeti / UTC günü", description: "Free politika rotalarının ortak günlük sağlayıcı maliyeti.", minimum: 0, maximum: 10_000_000_000_000, unit: "usd" },
      { key: "backboneCostNanoUsdPerDay", label: "Backbone maliyeti / UTC günü", description: "Tüm model rotalarının aşamayacağı en üst günlük maliyet sınırı.", minimum: 0, maximum: 100_000_000_000_000, unit: "usd" },
    ],
  },
];

const formatTimestamp = (value: number | null): string => value
  ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(value)
  : "Henüz yayımlanmadı";

const inputValue = (field: CostLimitFieldDefinition, limits: TayanCostLimits): number =>
  field.unit === "usd" ? limits[field.key] / 1_000_000_000 : limits[field.key];

const normalizedValue = (field: CostLimitFieldDefinition, value: number): number =>
  field.unit === "usd" ? Math.round(value * 1_000_000_000) : Math.round(value);

const validationError = (limits: TayanCostLimits): string | null => {
  for (const section of sections) {
    for (const field of section.fields) {
      const value = limits[field.key];
      if (!Number.isSafeInteger(value) || value < field.minimum || value > field.maximum) {
        const minimum = field.unit === "usd" ? field.minimum / 1_000_000_000 : field.minimum;
        const maximum = field.unit === "usd" ? field.maximum / 1_000_000_000 : field.maximum;
        return `${field.label} ${minimum.toLocaleString("tr-TR")}–${maximum.toLocaleString("tr-TR")} aralığında olmalı.`;
      }
    }
  }
  if (limits.accountRequestsPerMinute > limits.accountRequestsPerDay) return "Dakikalık hesap limiti günlük hesap limitinden yüksek olamaz.";
  if (limits.projectCostNanoUsdPerDay > limits.backboneCostNanoUsdPerDay) return "Backbone maliyet tavanı günlük AI maliyet tavanından düşük olamaz.";
  return null;
};

export function CostLimits() {
  const load = useCallback(() => readAdminData<CostLimitsData>({ view: "costLimits" }), []);
  const { data, error: loadError, isLoading, refresh } = useAdminRead({ load });
  const [limits, setLimits] = useState<TayanCostLimits | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data) setLimits(structuredClone(data.limits));
  }, [data]);

  const changed = useMemo(() => Boolean(data && limits && JSON.stringify(data.limits) !== JSON.stringify(limits)), [data, limits]);

  const update = (field: CostLimitFieldDefinition, raw: number) => {
    if (!limits || !Number.isFinite(raw)) return;
    setLimits({ ...limits, [field.key]: normalizedValue(field, raw) });
    setSaveError(null);
    setSuccess(null);
  };

  const save = async () => {
    if (!data || !limits || !changed) return;
    const invalid = validationError(limits);
    if (invalid) {
      setSaveError(invalid);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    setSuccess(null);
    try {
      const result = await publishCostLimits({ expectedRevision: data.revision, limits, requestId: crypto.randomUUID() });
      const current = await refresh();
      if (!current || current.revision !== result.revision) throw new Error("Yayımlanan revizyon geri okunamadı.");
      setSuccess(`Revizyon ${result.revision} yayımlandı. Yeni instance'lar hemen, sıcak instance'lar en geç 15 dakika içinde kullanır.`);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : "Maliyet limitleri yayımlanamadı.");
    } finally {
      setIsSaving(false);
    }
  };

  const error = saveError ?? loadError;
  return (
    <main className="page cost-limits-page">
      <section className="page-heading">
        <div><span className="breadcrumb">ALTYAPI / MALİYET LİMİTLERİ</span><h1>Maliyet limitleri</h1><p>Tayan AI kullanımı için hesap ve platform tavanlarını uygulama sürümü çıkarmadan yönetin.</p></div>
        <div className="snapshot"><strong>Revizyon {data?.revision ?? "—"}</strong><span>{formatTimestamp(data?.updatedAt ?? null)}</span><Button onClick={() => void refresh()} disabled={isLoading || isSaving} type="button" variant="soft"><ArrowClockwise size={18} /> Yenile</Button></div>
      </section>
      <div className="alert"><ShieldCheck size={21} /><span><strong>Yeni istekler güvenli tavanı kullanır</strong>0 girilen bütçe yeni AI analizlerini durdurur. Model seçimi, fiyat katsayıları ve geçmiş kullanım kayıtları değişmez.</span></div>
      {error ? <div className="alert danger" role="alert"><WarningCircle size={21} /><span><strong>Politika uygulanamadı</strong>{error}</span></div> : null}
      {success ? <div className="alert success" role="status"><ShieldCheck size={21} /><span><strong>Politika yayımlandı</strong>{success}</span></div> : null}
      <form onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <div className="cost-limit-sections">
          {sections.map((section) => <section className="panel cost-limit-section" key={section.title}><div className="panel-heading"><div><span className="kicker">CANLI POLİTİKA</span><h2>{section.title}</h2><p>{section.description}</p></div><CurrencyDollar size={22} /></div><div className="cost-limit-grid">{section.fields.map((field) => <label className="cost-limit-field" key={field.key}><span><strong>{field.label}</strong><small>{field.description}</small></span><NumberField ariaLabel={field.label} disabled={!limits || isLoading || isSaving} maximum={field.unit === "usd" ? field.maximum / 1_000_000_000 : field.maximum} minimum={field.unit === "usd" ? field.minimum / 1_000_000_000 : field.minimum} onValueChange={(value) => update(field, value)} step={field.unit === "usd" ? 0.001 : 1} unit={field.unit === "usd" ? "USD" : undefined} value={limits ? inputValue(field, limits) : ""} /></label>)}</div></section>)}
        </div>
        <section className="publish-bar"><div><strong>{data?.source === "published" ? "Yayımlanmış politika" : "Varsayılan politika"}</strong><span>{changed ? "Kaydedilmemiş değişiklikler var." : "Ekrandaki değerler canlı revizyonla aynı."}</span></div><Button disabled={!changed || isLoading || isSaving} type="submit"><FloppyDisk size={18} />{isSaving ? "Yayımlanıyor…" : "Limitleri yayımla"}</Button></section>
      </form>
    </main>
  );
}
